import * as React from 'react';

import {FileUploadModalPage} from 'shells/form/file-upload-modal-page';
import {Link} from 'shells/link/link';
import {Modal} from 'shells/modal/modal';
import {MarketingBody} from 'shells/typography/marketing-body';

import type {Audience} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../core/use-get-session-user';

import {Home, Import} from './pages';

type Props = {
    audience: Audience;
    isOpen: boolean;
    onClose: () => void;
};

export type Page = 'home' | 'import' | 'csv' | 'mailchimp' | 'ctct' | 'other';

export function AddToAudienceModal(props: Props) {
    const [page, setPage] = React.useState<Page>('home');
    const {user} = useGetSessionUser();
    const canImport = Boolean(user && user.permissions.canImport);

    return (
        <Modal
            headerBackButton={page !== 'home'}
            onHeaderBackButtonPress={() => {
                if (page === 'import') {
                    setPage('home');
                } else {
                    // Otherwise, we're on the nested import page, go back one
                    setPage('import');
                }
            }}
            headerText={getTitleTextForPage(page, props.audience)}
            isOpen={props.isOpen}
            onClose={props.onClose}
            // If the user can't import, they won't see the import option in the
            // add to audience modal, so we adjust the height to remove that empty space
            minHeight={!canImport && page === 'home' ? 366 : 636}
            maxHeight={!canImport && page === 'home' ? 366 : 636}
        >
            {page === 'home' ? (
                <Home
                    onSelectImport={() => {
                        setPage('import');
                    }}
                    audience={props.audience}
                    canImport={canImport}
                />
            ) : undefined}
            {page === 'import' ? (
                // @ts-expect-error
                <Import onBack={() => setPage('home')} onSelectImportType={setPage} />
            ) : undefined}
            {['csv', 'mailchimp', 'ctct', 'other'].includes(page) ? (
                <FileUploadModalPage
                    subheaderText={getSubheaderText(page)}
                    audienceId={props.audience.id}
                />
            ) : undefined}
        </Modal>
    );
}

function getSubheaderText(page: string) {
    switch (page) {
        case 'csv':
            return (
                <MarketingBody>
                    Once you upload your file, we’ll walk you through mapping your data to the right
                    places in Nutshell.
                </MarketingBody>
            );
        case 'mailchimp':
            return (
                <React.Fragment>
                    <MarketingBody>
                        Need some help getting your contacts into a CSV file?{' '}
                        <Link
                            href='https://mailchimp.com/help/view-export-contacts/'
                            size='big'
                            variant='primary'
                        >
                            Learn how to export from MailChimp
                        </Link>
                    </MarketingBody>
                    <MarketingBody>
                        Once you’ve got that file exported, upload it here and we’ll walk you
                        through mapping your data to the right places in Nutshell.
                    </MarketingBody>
                </React.Fragment>
            );
        case 'ctct':
            return (
                <React.Fragment>
                    <MarketingBody>
                        Need some help getting your contacts into a CSV file?{' '}
                        <Link
                            href='https://knowledgebase.constantcontact.com/articles/KnowledgeBase/5295-exporting-a-contact-list-from-constant-contact'
                            size='big'
                            variant='primary'
                        >
                            Learn how to export from Constant Contact
                        </Link>
                    </MarketingBody>
                    <MarketingBody>
                        Once you’ve got that file exported, upload it here and we’ll walk you
                        through mapping your data to the right places in Nutshell.
                    </MarketingBody>
                </React.Fragment>
            );
        case 'other':
            return (
                <React.Fragment>
                    <MarketingBody>
                        Start by exporting a CSV file of your contacts from your email marketing
                        tool.{' '}
                    </MarketingBody>
                    <MarketingBody>
                        Once you upload your file, we’ll walk you through mapping your data to the
                        right places in Nutshell.
                    </MarketingBody>
                </React.Fragment>
            );
        default:
            return (
                <MarketingBody>
                    Once you upload your file, we’ll walk you through mapping your data to the right
                    places in Nutshell.
                </MarketingBody>
            );
    }
}

function getTitleTextForPage(page: string, audience: Audience) {
    switch (page) {
        case 'home':
            return `Add members to ${audience.name}`;
        case 'import':
            return 'Where are your contacts coming from?';
        case 'csv':
            return 'Upload your spreadsheet to get started';
        case 'mailchimp':
            return 'Export your contacts from MailChimp first';
        case 'ctct':
            return 'Export your contacts from Constant Contact first';
        case 'other':
            return 'Another CRM?';
        default:
            return 'This shouldn’t happen';
    }
}
